import React, { lazy, Suspense, useState, useEffect, useCallback, useMemo } from 'react';
import { BrowserRouter, Routes, Route, Link, Navigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import './App.css';
import { AuthProvider, useAuth } from './context/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import LocaleSelector from './components/LocaleSelector';

// ── Page chunks — each route gets its own JS bundle loaded on demand ──────────
// Dashboard is the first authenticated page; prefetch removed intentionally —
// background parsing of the large recharts-containing chunk was contributing
// to TBT during Lighthouse's measurement window.
const Dashboard      = lazy(() => import('./pages/Dashboard'));
const ReportsPage    = lazy(() => import('./pages/ReportsPage'));
const CompliancePage = lazy(() => import('./pages/CompliancePage'));
const SettingsPage   = lazy(() => import('./pages/SettingsPage'));
const BillingPage    = lazy(() => import('./pages/BillingPage'));
const InvoicePage    = lazy(() => import('./pages/InvoicePage'));
const LoginPage      = lazy(() => import('./pages/LoginPage'));
const RegisterPage   = lazy(() => import('./pages/RegisterPage'));
const OnboardingPage = lazy(() => import('./pages/OnboardingPage'));
const VerifyPage     = lazy(() => import('./pages/VerifyPage'));
const SplashPage     = lazy(() => import('./pages/SplashPage'));
const AboutPage      = lazy(() => import('./pages/AboutPage'));
const DeveloperPage  = lazy(() => import('./pages/DeveloperPage'));
const CareersPage    = lazy(() => import('./pages/CareersPage'));

// ── SVG nav icons ─────────────────────────────────────────────────────────────
const IconOverview    = () => (<svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="2" width="7" height="7" rx="1.2"/><rect x="11" y="2" width="7" height="7" rx="1.2"/><rect x="2" y="11" width="7" height="7" rx="1.2"/><rect x="11" y="11" width="7" height="7" rx="1.2"/></svg>);
const IconReports     = () => (<svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M4 4h8l4 4v9a1 1 0 01-1 1H4a1 1 0 01-1-1V5a1 1 0 011-1z"/><path d="M12 4v4h4M7 10h6M7 13h4"/></svg>);
const IconCompliance  = () => (<svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M10 2L3 5.5v5c0 3.6 3 6.8 7 7.5 4-0.7 7-3.9 7-7.5v-5L10 2z"/><path d="M7 10l2 2 4-4"/></svg>);
const IconVerify      = () => (<svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><circle cx="10" cy="10" r="8"/><path d="M7 10l2 2 4-4"/></svg>);
const IconSettings    = () => (<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>);
const IconBilling     = () => (<svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><rect x="1" y="4" width="18" height="13" rx="2"/><path d="M1 8h18"/><path d="M5 12h2M9 12h2"/></svg>);
const IconInvoice     = () => (<svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="2" width="14" height="16" rx="1.5"/><path d="M7 6h6M7 9h6M7 12h3"/><path d="M13 14l1.5 1.5L17 13"/></svg>);
const IconDevDocs     = () => (<svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"><path d="M4 3h8l4 4v11a1 1 0 01-1 1H4a1 1 0 01-1-1V4a1 1 0 011-1z"/><path d="M12 3v4h4"/><path d="M7 9h6M7 12h4"/><circle cx="14.5" cy="14.5" r="2.5"/><path d="M16.5 16.5l2 2"/></svg>);
const IconExternalLink = () => (<svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" style={{ width: '0.6rem', height: '0.6rem', flexShrink: 0, opacity: 0.6 }}><path d="M11 3h6v6M8 12L17 3M9 5H4a1 1 0 00-1 1v10a1 1 0 001 1h10a1 1 0 001-1v-5"/></svg>);
const IconChevronLeft = () => (<svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M13 4l-6 6 6 6"/></svg>);
const IconChevronRight= () => (<svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M7 4l6 6-6 6"/></svg>);
const IconMenu        = () => (<svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><path d="M3 5h14M3 10h14M3 15h14"/></svg>);
const IconSun         = () => (<svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"><circle cx="10" cy="10" r="3.5"/><path d="M10 2v2M10 16v2M2 10h2M16 10h2M4.6 4.6l1.4 1.4M14 14l1.4 1.4M4.6 15.4l1.4-1.4M14 6l1.4-1.4"/></svg>);
const IconMoon        = () => (<svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"><path d="M17 11A7 7 0 019 3a7 7 0 100 14 7 7 0 008-6z"/></svg>);

// ── Sidebar component ─────────────────────────────────────────────────────────
const Sidebar = React.memo(function Sidebar({ collapsed, onCollapse, onClose, navItems, activePath, theme, onToggleTheme, user, onLogout, showClose }) {
  const { t } = useTranslation();
  return (
    <aside className={`sidebar${collapsed ? ' collapsed' : ''}`}>
      <div className="sidebar-header">
        {showClose && (
          <button
            className="sidebar-close-btn"
            onClick={onClose}
            aria-label={t('sidebar.collapseSidebar')}
          >
            <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M5 5l10 10M15 5L5 15"/></svg>
          </button>
        )}
        {!showClose && (
          <button
            className="sidebar-collapse-btn"
            onClick={onCollapse}
            title={collapsed ? t('sidebar.expandSidebar') : t('sidebar.collapseSidebar')}
          >
            {collapsed ? <IconChevronRight /> : <IconChevronLeft />}
          </button>
        )}
      </div>

      <nav className="sidebar-nav">
        {navItems.map(n => {
          const isActive = activePath.startsWith(n.to);
          if (n.newTab) {
            return (
              <a
                key={n.to}
                href={n.to}
                target="_blank"
                rel="noopener noreferrer"
                className={`sidebar-link${isActive ? ' active' : ''}`}
                title={n.label}
                onClick={onClose}
              >
                <span className="sidebar-link-icon">{n.icon}</span>
                {!collapsed && <span className="sidebar-link-label">{n.label}<IconExternalLink /></span>}
              </a>
            );
          }
          return (
            <Link
              key={n.to}
              to={n.to}
              className={`sidebar-link${isActive ? ' active' : ''}`}
              title={n.label}
              onClick={onClose}
            >
              <span className="sidebar-link-icon">{n.icon}</span>
              {!collapsed && <span className="sidebar-link-label">{n.label}</span>}
              {isActive && <span className="sidebar-link-indicator" />}
            </Link>
          );
        })}
      </nav>

      <div className="sidebar-footer">
        <button
          className="sidebar-theme-btn"
          onClick={onToggleTheme}
          title={theme === 'dark' ? t('sidebar.switchToLight') : t('sidebar.switchToDark')}
        >
          <span className="sidebar-link-icon">{theme === 'dark' ? <IconSun /> : <IconMoon />}</span>
          {!collapsed && <span className="sidebar-link-label">{theme === 'dark' ? t('sidebar.lightMode') : t('sidebar.darkMode')}</span>}
        </button>

        {user && !collapsed && (
          <div className="sidebar-user">
            <div className="sidebar-user-name">{user.username}</div>
            <button className="logout-btn" onClick={onLogout}>{t('sidebar.signOut')}</button>
          </div>
        )}
        {user && collapsed && (
          <button className="sidebar-logout-icon" onClick={onLogout} title={t('sidebar.signOut')}>
            <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
              <path d="M8 3H4a1 1 0 00-1 1v12a1 1 0 001 1h4M13 14l3-4-3-4M16 10H7"/>
            </svg>
          </button>
        )}
      </div>
    </aside>
  );
});

// ── AppShell ──────────────────────────────────────────────────────────────────
// Routes that don't require the sidebar/shell
const PUBLIC_PATHS = ['/', '/login', '/register', '/onboarding', '/home', '/verify', '/about', '/developer', '/careers'];

function AppShell() {
  const location = useLocation();
  const { user, logout, sessionExpiring, extendSession } = useAuth();
  const { t } = useTranslation();

  const [theme, setTheme] = useState(
    () => localStorage.getItem('trustify_theme') || 'dark'
  );
  const [collapsed, setCollapsed] = useState(
    () => localStorage.getItem('sidebar_collapsed') === 'true'
  );
  const [drawerOpen, setDrawerOpen] = useState(false);

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem('trustify_theme', theme);
  }, [theme]);

  // Pause CSS animations (scan, shimmer) while the tab is not visible
  useEffect(() => {
    const onVisibility = () =>
      document.body.classList.toggle('page-hidden', document.hidden);
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, []);

  // Close drawer on navigation and scroll to top
  useEffect(() => { setDrawerOpen(false); window.scrollTo(0, 0); }, [location.pathname]);

  const toggleTheme  = useCallback(() => setTheme(t => t === 'dark' ? 'light' : 'dark'), []);
  const toggleCollapse = useCallback(() => setCollapsed(c => {
    localStorage.setItem('sidebar_collapsed', String(!c));
    return !c;
  }), []);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const navItems = useMemo(() => [
    { to: '/dashboard',  label: t('nav.overview'),   icon: <IconOverview /> },
    { to: '/reports',    label: t('nav.reports'),    icon: <IconReports /> },
    { to: '/compliance', label: t('nav.compliance'), icon: <IconCompliance /> },
    { to: '/verify',     label: t('nav.verify'),     icon: <IconVerify />, newTab: true },
    { to: '/settings',   label: t('nav.settings'),   icon: <IconSettings />, authRequired: true },
    { to: '/billing',    label: t('nav.billing'),    icon: <IconBilling />,  authRequired: true },
    { to: '/invoice',    label: t('nav.invoice'),    icon: <IconInvoice />,  authRequired: true },
    { to: '/developer',  label: t('nav.devDocs'),    icon: <IconDevDocs />,  newTab: true },
  ].filter(n => !n.authRequired || user), [t, user]);

  const toggleDrawer = useCallback(() => setDrawerOpen(o => !o), []);

  const closeSidebar = useCallback(() => setDrawerOpen(false), []);

  const isPublicPage = PUBLIC_PATHS.includes(location.pathname);

  if (isPublicPage) {
    return (
      <Suspense fallback={<div className="page-loading" />}>
        <Routes>
          <Route path="/"           element={<SplashPage />} />
          <Route path="/home"        element={<SplashPage />} />
          <Route path="/login"       element={<LoginPage />} />
          <Route path="/register"    element={<RegisterPage />} />
          <Route path="/onboarding"  element={<OnboardingPage />} />
          <Route path="/verify"      element={<VerifyPage />} />
          <Route path="/about"       element={<AboutPage />} />
          <Route path="/careers"     element={<CareersPage />} />
          <Route path="/developer"   element={<DeveloperPage />} />
        </Routes>
      </Suspense>
    );
  }

  const sidebarProps = {
    collapsed,
    onCollapse: toggleCollapse,
    onClose: closeSidebar,
    navItems,
    activePath: location.pathname,
    theme,
    onToggleTheme: toggleTheme,
    user,
    onLogout: logout,
  };

  return (
    <div className="app-shell">
      {/* ── Global topbar (always visible) ── */}
      <header className="topbar">
        {user && (
          <button
            className="topbar-menu-btn"
            onClick={toggleDrawer}
            aria-label={t('topbar.openNavigation')}
          >
            <IconMenu />
          </button>
        )}
        <Link to="/" className="topbar-brand-link" style={{ textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 8 }}>
          <div style={{
            width: 30, height: 30, borderRadius: 7, flexShrink: 0,
            background: 'linear-gradient(135deg, #00d4ff, #3d8bff)',
            display: 'grid', placeItems: 'center',
            fontFamily: "'JetBrains Mono', monospace", fontWeight: 700, fontSize: 14, color: '#000',
            boxShadow: '0 0 0.75rem rgba(0,212,255,0.35)',
          }}>T</div>
          <div className="brand-name topbar-brand">Trust<span>ify</span></div>
        </Link>
        <div className="topbar-right">
          <a
            href="/developer"
            target="_blank"
            rel="noopener noreferrer"
            className="topbar-developer-link"
          >
            <IconDevDocs />
            <span>Developer</span>
          </a>
          <button className="topbar-theme-btn" onClick={toggleTheme} title={t('topbar.toggleTheme')}>
            {theme === 'dark' ? <IconSun /> : <IconMoon />}
          </button>
          {user && (
            <div className="topbar-user">
              <span className="user-greeting">{t('topbar.greeting', { username: user.username })}</span>
              <button className="logout-btn" onClick={logout}>{t('topbar.signOut')}</button>
            </div>
          )}
        </div>
      </header>

      <div className={`app-layout${collapsed ? ' sidebar-is-collapsed' : ''}`}>
        {/* ── Desktop sidebar (authenticated only) ── */}
        {user && (
          <div className="sidebar-wrap-desktop">
            <Sidebar {...sidebarProps} />
          </div>
        )}

        {/* ── Mobile drawer (authenticated only) ── */}
        {user && drawerOpen && (
          <>
            <div className="sidebar-backdrop" onClick={closeSidebar} />
            <div className="sidebar-wrap-mobile">
              <Sidebar {...{ ...sidebarProps, collapsed: false, showClose: true }} />
            </div>
          </>
        )}

        {/* ── Main content ── */}
        <div className="app-content">
          {/* ── Session-expiry warning banner ── */}
          {user && sessionExpiring && (
            <div className="session-expiry-banner" role="alert">
              <span>⚠ Your session will expire in 5 minutes due to inactivity.</span>
              <div className="session-expiry-actions">
                <button className="session-expiry-stay" onClick={extendSession}>Stay logged in</button>
                <button className="session-expiry-logout" onClick={logout}>Log out now</button>
              </div>
            </div>
          )}

          <main className="app-main">
            <Suspense fallback={<div className="page-loading" />}>
              <Routes>
                <Route path="/dashboard"  element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
                <Route path="/reports"    element={<ProtectedRoute><ReportsPage /></ProtectedRoute>} />
                <Route path="/compliance" element={<ProtectedRoute><CompliancePage /></ProtectedRoute>} />
                <Route path="/settings"   element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
                <Route path="/billing"    element={<ProtectedRoute><BillingPage /></ProtectedRoute>} />
                <Route path="/invoice"    element={<ProtectedRoute><InvoicePage /></ProtectedRoute>} />
                <Route path="*" element={<Navigate to="/dashboard" replace />} />
              </Routes>
            </Suspense>
          </main>

          <footer className="app-footer">
            <span>{t('footer.copyright')}</span>
            <LocaleSelector />
          </footer>
        </div>
      </div>
    </div>
  );
}

export default function App() {
  return (
    <BrowserRouter future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <AuthProvider>
        <AppShell />
      </AuthProvider>
    </BrowserRouter>
  );
}
